'use client';

import type { ReactNode } from 'react';

import Link from '~/(tenant)/_components/tenant-link';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import { DirectionalArrow } from './directional-arrow';
import { Eyebrow } from './eyebrow';
import { QuietMark } from './ornament';

/**
 * Magazine-style empty state. Mark + eyebrow + serif headline (with optional
 * italic emphasis) + body copy + optional CTA. Used by every section that
 * can render zero items.
 *
 * Example:
 *   <EmptyState
 *     mark="quiet"
 *     eyebrow="A blank calendar"
 *     title="Your schedule is"
 *     emphasis="quiet."
 *     body="When you reserve a class, it will appear here."
 *     cta={{ label: 'Reserve a class', href: '/book' }}
 *   />
 */
export function EmptyState({
  mark = 'quiet',
  eyebrow,
  title,
  emphasis,
  body,
  cta,
  compact = false,
  paddingY = compact ? 28 : 48,
}: {
  mark?: 'quiet' | 'none';
  eyebrow?: ReactNode;
  title?: ReactNode;
  emphasis?: ReactNode;
  body?: ReactNode;
  cta?: { label: ReactNode; href: string };
  compact?: boolean;
  paddingY?: number;
}) {
  const t = useNativeTokens();

  return (
    <div
      style={{
        textAlign: 'center',
        padding: `${paddingY}px 32px`,
      }}
    >
      {mark === 'quiet' && (
        <div style={{ marginBottom: compact ? 16 : 28, opacity: 0.85 }}>
          <QuietMark size={compact ? 64 : 108} />
        </div>
      )}

      {eyebrow && <Eyebrow style={{ marginBottom: 14 }}>{eyebrow}</Eyebrow>}

      {(title || emphasis) && (
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: compact ? 22 : 28,
            fontWeight: 300,
            color: t.foreground,
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
          {title && emphasis ? <br /> : null}
          {emphasis && <em style={{ fontStyle: 'italic' }}>{emphasis}</em>}
        </div>
      )}

      {body && (
        <p
          style={{
            fontFamily: t.fontSans,
            fontSize: 13,
            color: t.muted,
            marginTop: 16,
            lineHeight: 1.55,
            maxWidth: 280,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {body}
        </p>
      )}

      {cta && (
        <Link
          href={cta.href}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            marginTop: compact ? 20 : 28,
            padding: '14px 28px',
            backgroundColor: t.accent,
            color: t.background,
            borderRadius: t.radius,
            fontFamily: t.fontSans,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            textDecoration: 'none',
          }}
        >
          {cta.label}
          <DirectionalArrow size={12} strokeWidth={1.4} />
        </Link>
      )}
    </div>
  );
}
